import { forwardRef } from "react";
import {
  ListItemProps as ChakraListItemProps,
  List as ChakraList,
} from "@chakra-ui/react";

import type { ListItemSharedProps } from "./ListItem.props";

export type ListItemProps = ListItemSharedProps &
  ChakraListItemProps & {
    // Add any web specific props here.
  };

const ListItem = forwardRef<HTMLLIElement, ListItemProps>((props, ref) => {
  const {
    isSelected = false, // default value
    isDisabled = false, // default value
    variant = "primary", // default value
    children,
    ...restProps
  } = props;

  const getStyles = () => {
    // Text color
    let color = "var(--chakra-colors-black)";
    if (isDisabled) {
      color = "var(--chakra-colors-gray-500)";
    } else if (
      isSelected &&
      (variant === "primary" || variant === "secondary")
    ) {
      color = "var(--chakra-colors-white)";
    }

    // Background color - isDisabled takes precedence over isSelected
    let backgroundColor =
      "var(--chakra-colors-bg-subtle, var(--chakra-colors-gray-50))";
    if (isDisabled) {
      backgroundColor = "var(--chakra-colors-gray-200)";
    } else if (isSelected) {
      backgroundColor =
        variant === "primary"
          ? "var(--chakra-colors-primary-500)"
          : "var(--chakra-colors-primary-600)";
    }

    // Border
    let border = "none";
    if (variant === "outline") {
      border = isSelected
        ? "1px solid var(--chakra-colors-primary-500)"
        : "1px solid var(--clr-font-light, var(--chakra-colors-gray-300))";
    }

    // Hover background
    let hoverBg = "var(--chakra-colors-gray-100)";
    if (isDisabled || isSelected) {
      hoverBg = backgroundColor; // Keep current bg on hover
    }

    return { color, backgroundColor, border, hoverBg };
  };

  const styles = getStyles();

  return (
    <ChakraList.Item
      ref={ref}
      listStyleType="none"
      display="flex"
      alignItems="center"
      borderRadius={variant === "outline" ? "6px" : "none"}
      transition="background-color 0.15s ease"
      {...restProps}
      css={{
        cursor: isDisabled ? "not-allowed !important" : "pointer",
        color: `${styles.color} !important`,
        backgroundColor: isDisabled
          ? "transparent"
          : `${styles.backgroundColor} !important`,
        border: `${styles.border} !important`,
        "&:hover": {
          backgroundColor: `${styles.hoverBg} !important`,
          color: `${styles.color} !important`,
          cursor: isDisabled ? "not-allowed !important" : "pointer",
        },
      }}
    >
      {children}
    </ChakraList.Item>
  );
});

ListItem.displayName = "ListItem";

export default ListItem;

export function BulletedListItem(props: ListItemProps) {
  return <ListItem {...props} display="li" listStyleType="inherit" />;
}
